import React from 'react';
import { motion } from 'framer-motion';
import { useSite } from '../content/SiteContext';

const FOOTER_NAV = ['inicio', 'servicios', 'proyectos', 'premios'];

const Footer = () => {
  const { content, t } = useSite();
  const footer = content.footer;
  const container = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6, 
        ease: [0.33, 1, 0.68, 1] 
      } 
    }
  };

  return (
    <motion.footer
      className="relative bg-gradient-to-b from-gray-900 to-gray-950 text-white pt-12 md:pt-16 pb-6 md:pb-8 overflow-hidden"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      {/* Elementos decorativos de fondo */}
      <div className="absolute top-0 left-0 w-full h-full -z-10">
        <div className="absolute top-10 left-10 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-56 h-56 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/3 right-1/4 w-40 h-40 bg-indigo-500/5 rounded-full blur-3xl"></div>
      </div>
      {/* Patrón de puntos decorativos */}
      <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.05)_1px,_transparent_1px)] bg-[length:20px_20px] -z-10"></div>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 lg:gap-6 mb-8 md:mb-12">
          {/* Logo */}
          <motion.div 
            className="flex flex-col items-center md:items-start"
            variants={item}
            initial="hidden"
            animate="visible"
          >
            <div className="flex items-center mb-4">
              {/* Logo de la empresa */}
              <div className="bg-gray-800/30 backdrop-blur-sm border border-gray-700 rounded-xl p-3 md:p-4">
                <div className="flex flex-col md:flex-row items-center">
                  <img src="/logo2.png" alt="Logo 8bits" className="h-8 md:h-12 w-auto rounded-lg" />
                  <div className="mt-2 md:mt-0 md:ml-3 text-center md:text-left">
                    <p className="text-xs text-gray-500">{t(footer.tagline)}</p>
                  </div>
                </div>
              </div>
            </div>
            {/* Texto descriptivo - visible solo en móvil */}
            <div className="md:hidden mt-4 text-center">
              <p className="text-gray-400 text-xs md:text-sm leading-relaxed max-w-xs">
                {t(footer.description)}
              </p>
            </div>
          </motion.div>

          {/* Texto descriptivo - visible solo en desktop, entre logo y navegación */}
          <motion.div 
            className="hidden md:flex items-center justify-start"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <p className="text-gray-400 text-sm leading-relaxed max-w-md text-left">
              {t(footer.description)}
            </p>
          </motion.div>

          {/* Navegación */}
          <motion.div 
            variants={container}
            initial="hidden"
            animate="visible"
            className="flex justify-center"
          >
            <div>
              <h3 className="text-base md:text-lg font-semibold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400 text-center md:text-left">
                {t(footer.navTitle)}
              </h3>
              <ul className="flex flex-wrap gap-4 md:gap-6 justify-center md:justify-start">
                {FOOTER_NAV.map((itemName) => (
                  <motion.li 
                    key={itemName}
                    variants={item}
                  >
                    <a 
                      href={`#${itemName}`}
                      className="text-gray-400 hover:text-purple-400 transition-colors duration-300 flex items-center group text-sm md:text-base"
                    >
                      <span className="w-2 h-2 bg-gray-600 rounded-full mr-2 md:mr-3 group-hover:bg-purple-500 transition-colors"></span>
                      {t(content.nav[itemName])}
                    </a>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
        
        {/* Línea divisoria */}
        <div className="border-t border-gray-800 my-6 md:my-8"></div>
        {/* Copyright */}
        <motion.div 
          className="flex flex-col md:flex-row justify-between items-center pt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-gray-500 text-xs md:text-sm mb-4 md:mb-0 text-center md:text-left">
            {t(footer.copyright)}
          </p>
          <div className="flex space-x-4 md:space-x-6">
            {footer.termsUrl ? (
              <a href={footer.termsUrl} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 text-xs md:text-sm transition-colors">{t(footer.terms)}</a>
            ) : (
              <span className="text-gray-500 text-xs md:text-sm">{t(footer.terms)}</span>
            )}
            {footer.privacyUrl ? (
              <a href={footer.privacyUrl} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-300 text-xs md:text-sm transition-colors">{t(footer.privacy)}</a>
            ) : (
              <span className="text-gray-500 text-xs md:text-sm">{t(footer.privacy)}</span>
            )}
          </div>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer; 